import { useEffect, useState, useRef } from 'react';
import { CssVarsProvider, CssBaseline, Container, Button, Box, Sheet, Typography, extendTheme } from '@mui/joy';
import SummaryHeader from './components/SummaryHeader';
import DailyExpenses from './components/DailyExpenses';
import MonthlyCategories from './components/MonthlyCategories';
import MonthlyExpenses from './components/MonthlyExpenses';
import BudgetsTab from './components/BudgetsTab';
import AccountsTab from './components/AccountsTab';
import AddExpenseForm from './components/AddExpenseForm';

// Контейнер прокрутки модалок Joy по умолчанию занимает всю высоту layout-
// viewport (position: fixed; inset: 0). Когда открывается экранная клавиатура,
// эта высота не меняется, и модалка остаётся отцентрованной по всему экрану —
// нижняя часть (например кнопка «Перевести») прячется под клавиатурой и до неё
// нельзя доскроллить. Привязываем контейнер к реальной видимой области, чтобы
// модалка центрировалась в видимой зоне и прокручивалась над клавиатурой.
const theme = extendTheme({
    components: {
        JoyModalOverflow: {
            styleOverrides: {
                root: {
                    top: 'var(--app-vv-top, 0px)',
                    height: 'var(--app-vvh, 100%)',
                    bottom: 'auto',
                },
            },
        },
    },
});

const NAV = [
    { value: 'daily', label: 'Сегодня', icon: '📆' },
    { value: 'monthly', label: 'Все траты', icon: '📊' },
    { value: 'budgets', label: 'Бюджет', icon: '🎯' },
    { value: 'accounts', label: 'Счета', icon: '💳' }
];

function App() {
    const [initData, setInitData] = useState('');
    const [activeTab, setActiveTab] = useState('daily');
    const [addExpenseOpen, setAddExpenseOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const dailyExpensesRef = useRef(null);
    const monthlyCategoriesRef = useRef(null);
    const monthlyExpensesRef = useRef(null);
    const summaryRef = useRef(null);

    useEffect(() => {
        const tg = window.Telegram?.WebApp;
        if (!tg) {
            console.log('Standalone mode');
            // Для тестирования без Telegram
            setInitData('test');
            return;
        }

        tg.disableClosingConfirmation();
        tg.expand();
        tg.ready();
        setInitData(tg.initData);
        tg.setBackgroundColor('#f5f5f5');
    }, []);

    const handleExpenseChanged = () => {
        // Обновляем сводку и все списки трат
        summaryRef.current?.refresh();
        dailyExpensesRef.current?.refresh();
        monthlyCategoriesRef.current?.refresh();
        monthlyExpensesRef.current?.refresh();
    };

    const openCreate = () => {
        setEditingExpense(null);
        setAddExpenseOpen(true);
    };

    const openEdit = (expense) => {
        setEditingExpense(expense);
        setAddExpenseOpen(true);
    };

    const closeForm = () => {
        setAddExpenseOpen(false);
        setEditingExpense(null);
    };

    return (
        <CssVarsProvider theme={theme}>
            <CssBaseline />
            <Container maxWidth="md" sx={{ mt: 2, mb: 12, pb: 4 }}>
                {initData ? (
                    <>
                        {activeTab === 'daily' && (
                            <>
                                <SummaryHeader ref={summaryRef} initDataRaw={initData} />
                                <DailyExpenses
                                    ref={dailyExpensesRef}
                                    initDataRaw={initData}
                                    onExpenseDeleted={handleExpenseChanged}
                                    onEditExpense={openEdit}
                                />
                                <MonthlyCategories ref={monthlyCategoriesRef} initDataRaw={initData} />
                            </>
                        )}
                        {activeTab === 'monthly' && (
                            <MonthlyExpenses
                                ref={monthlyExpensesRef}
                                initDataRaw={initData}
                                onExpenseDeleted={handleExpenseChanged}
                                onEditExpense={openEdit}
                            />
                        )}
                        {activeTab === 'budgets' && <BudgetsTab initDataRaw={initData} />}
                        {activeTab === 'accounts' && <AccountsTab initDataRaw={initData} />}
                    </>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        Инициализация...
                    </Box>
                )}
            </Container>

            {/* Плавающая кнопка добавления траты (над нижней навигацией) */}
            {(
                <Button
                    color="primary"
                    onClick={openCreate}
                    aria-label="Добавить трату"
                    sx={{
                        position: 'fixed',
                        bottom: 84,
                        right: 24,
                        width: 56,
                        height: 56,
                        minWidth: 56,
                        borderRadius: '50%',
                        fontSize: '28px',
                        fontWeight: 300,
                        boxShadow: 'md',
                        p: 0,
                        zIndex: 1001
                    }}
                >
                    +
                </Button>
            )}

            {/* Нижняя навигация (под большой палец) */}
            <Sheet
                component="nav"
                variant="outlined"
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'space-around',
                    borderLeft: 0,
                    borderRight: 0,
                    borderBottom: 0,
                    py: 0.5,
                    pb: 'calc(env(safe-area-inset-bottom, 0px) + 4px)',
                    bgcolor: 'background.surface',
                    zIndex: 1000
                }}
            >
                {NAV.map((item) => (
                    <Button
                        key={item.value}
                        variant="plain"
                        color={activeTab === item.value ? 'primary' : 'neutral'}
                        onClick={() => setActiveTab(item.value)}
                        aria-current={activeTab === item.value ? 'page' : undefined}
                        sx={{ flexDirection: 'column', gap: 0.25, minWidth: 64, px: 1, py: 0.5 }}
                    >
                        <Box sx={{ fontSize: 20, lineHeight: 1 }} aria-hidden="true">{item.icon}</Box>
                        <Typography level="body-xs" sx={{ color: 'inherit' }}>{item.label}</Typography>
                    </Button>
                ))}
            </Sheet>

            {/* Форма добавления/редактирования траты */}
            <AddExpenseForm
                open={addExpenseOpen}
                onClose={closeForm}
                initDataRaw={initData}
                onExpenseAdded={handleExpenseChanged}
                editExpense={editingExpense}
            />
        </CssVarsProvider>
    );
}

export default App;
